<template>
  <!-- "What happened at this spot?" — asked when a save changes a zone's name, type,
       area, counting line or counting direction, and for every zone on a camera that
       was moved (settings step 2). One answer per zone. -->
  <div class="zcq-overlay" role="dialog" aria-modal="true" aria-labelledby="zcq-title">
    <div class="zcq-card">
      <h3 id="zcq-title" class="zcq-title">{{ title || $t('zoneSettings.change.title') }}</h3>
      <p class="zcq-lead">{{ lead || $t('zoneSettings.change.lead') }}</p>

      <!-- "The camera was moved": one answer for all zones, changeable per zone below. -->
      <div v-if="allAtOnce && items.length > 1" class="zcq-all">
        <span class="zcq-all-label">{{ $t('zoneSettings.change.sameForAll') }}</span>
        <div class="zcq-all-opts">
          <button v-for="opt in options" :key="opt.kind" type="button" class="zcq-all-btn"
                  :class="{ chosen: allKind === opt.kind }" @click="answerAll(opt.kind)">
            {{ opt.label }}
          </button>
        </div>
      </div>

      <section v-for="item in items" :key="item.oldId" class="zcq-zone">
        <div class="zcq-zone-head">
          <span class="zcq-zone-name">{{ item.title }}</span>
          <span class="zcq-changes">{{ item.changes.length ? item.changes.join(' · ') : $t('zoneSettings.change.notRedrawn') }}</span>
        </div>
        <label v-for="opt in options" :key="opt.kind" class="zcq-opt"
               :class="{ chosen: answers[item.oldId] === opt.kind }">
          <input type="radio" :name="`zcq-${item.oldId}`" :value="opt.kind" v-model="answers[item.oldId]">
          <span class="zcq-opt-text">
            <span class="zcq-opt-label">{{ opt.label }}</span>
            <span class="zcq-opt-hint">{{ opt.hint }}</span>
            <span v-if="item.suggested === opt.kind" class="zcq-suggest">{{ $t('zoneSettings.change.noOverlap') }}</span>
          </span>
        </label>
      </section>

      <div class="zcq-actions">
        <button type="button" class="btn-outline" @click="$emit('cancel')">{{ $t('zoneSettings.common.cancel') }}</button>
        <button type="button" class="btn-solid ok" :disabled="!complete" @click="$emit('answer', { ...answers })">
          {{ $t('zoneSettings.common.save') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, computed, ref } from 'vue'
import { CHANGE_OPTIONS } from '@/zones/changeQuestion'

const props = defineProps({
  // [{ oldId, title, changes: [string], suggested: 'different' | null }]
  items: { type: Array, required: true },
  options: { type: Array, default: () => CHANGE_OPTIONS },
  // Empty: the default question, in the portal's language (zoneSettings.change.title / .lead).
  title: { type: String, default: '' },
  lead: { type: String, default: '' },
  allAtOnce: { type: Boolean, default: false },
})
defineEmits(['answer', 'cancel'])

// Nothing pre-selected, except "a different spot" when the areas do not overlap at all.
const answers = reactive(Object.fromEntries(props.items.map((i) => [i.oldId, i.suggested || null])))
const complete = computed(() => props.items.every((i) => !!answers[i.oldId]))

const allKind = ref(null)
const answerAll = (kind) => {
  allKind.value = kind
  for (const i of props.items) answers[i.oldId] = kind
}
</script>

<style scoped>
.zcq-overlay { position: fixed; inset: 0; background: rgba(13, 38, 76, .35); display: flex;
               align-items: center; justify-content: center; z-index: 2100; padding: 16px; }
.zcq-card { background: #fff; border-radius: 14px; max-width: 620px; width: 100%; max-height: 90vh;
            overflow: auto; padding: 22px 24px; box-shadow: 0 12px 40px rgba(13, 38, 76, .25); }
.zcq-title { margin: 0 0 4px; font-size: 1.15rem; color: #1f3b57; }
.zcq-lead { margin: 0 0 14px; color: #5b6b7b; font-size: .9rem; }
.zcq-all { background: #f5f9fd; border: 1px solid #dbe7f3; border-radius: 10px; padding: 10px 12px; }
.zcq-all-label { display: block; font-weight: 600; font-size: .88rem; color: #1f3b57; margin-bottom: 6px; }
.zcq-all-opts { display: flex; flex-wrap: wrap; gap: 6px; }
.zcq-all-btn { border: 1px solid #c9d8e8; background: #fff; color: #2C5F86; border-radius: 8px;
               padding: 4px 10px; font-size: .82rem; cursor: pointer; }
.zcq-all-btn.chosen { border-color: #4F7EB3; background: #4F7EB3; color: #fff; }
.zcq-zone { border-top: 1px solid #eef3f7; padding-top: 12px; margin-top: 12px; }
.zcq-zone-head { display: flex; flex-wrap: wrap; gap: 4px 12px; align-items: baseline; margin-bottom: 8px; }
.zcq-zone-name { font-weight: 700; color: #2C5F86; }
.zcq-changes { font-size: .82rem; color: #6b7b8b; }
.zcq-opt { display: flex; gap: 10px; align-items: flex-start; padding: 8px 10px; border: 1px solid #e3eaf1;
           border-radius: 10px; margin-bottom: 6px; cursor: pointer; }
.zcq-opt.chosen { border-color: #4F7EB3; background: #f5f9fd; }
.zcq-opt input { margin-top: 4px; }
.zcq-opt-text { display: flex; flex-direction: column; flex: 1; }
.zcq-opt-label { font-weight: 600; font-size: .92rem; color: #22313f; }
.zcq-opt-hint { font-size: .82rem; color: #5b6b7b; }
.zcq-suggest { font-size: .72rem; color: #2C5F86; background: #eef4fb; border-radius: 8px; padding: 2px 6px;
               align-self: flex-start; margin-top: 4px; }
.zcq-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 16px; }
</style>
